import { DependencyList, useCallback } from 'react'
import {
  ApiGetWithToken,
	ApiDeleteWithToken,
	ApiGetFileWithToken,
	ApiPatchWithToken,
	ApiPostFileWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost,
	useApiFetchList,
	FileResponse
} from 'shared/api'
import {
  searchApi,
  searchApiBy
} from 'interface/endpoints'
import {
	SearchFormData,
	SearchDescriptionFormData,
	SearchStatusFormData,
	SearchFactorFormData,
  SearchResult,
  SearchResultList,
	SearchRecordResult,
	SearchDetailResult,
	SearchsOptionResult,
	SearchsOptionFormData
} from './types'
import { GroupResultList } from 'interface/user/group'
import { useUpdateTime } from 'interface/util'

export * from './types'

export const useListSearch = (
	option: {
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: SearchRecordResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { onPreFetch, onFetched, partialLimit } = option
	return useApiFetchList<SearchRecordResult, SearchRecordResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					searchApi('/count'),
					cancelToken
				)
			},
			[onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				const wrapper = async () => {
					const list = await ApiGetWithToken<SearchResultList>(
						apiAccess,
						searchApi('/list', { offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 100,
		deps
	)
}

export const useGetSearch = (
  searchId: number | null | undefined,
	deps?: DependencyList
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!searchId) return null
      return ApiGetWithToken<SearchResult>(
        apiAccess,
        searchApiBy(searchId),
        cancelToken
  		)
    },
		[searchId]
	),
	deps
)

export const useGetSearchDetail = (
  searchId: number | null | undefined,
	deps?: DependencyList
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!searchId) return null
      return ApiGetWithToken<SearchDetailResult>(
        apiAccess,
        searchApiBy(searchId, '/detail'),
        cancelToken
  		)
    },
		[searchId]
	),
	deps
)

export const usePostSearch = (
  callback?: {
    onPosted?: (result: SearchResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchFormData, SearchResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        searchApi(),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSearch = (
	searchId: number, 
  callback?: {
    onEditted?: (result: SearchResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchFormData, SearchResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        searchApiBy(searchId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSearchDescription = (
	searchId: number,
  callback?: {
    onEditted?: (result: SearchResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchDescriptionFormData, SearchResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        searchApiBy(searchId, "/description"),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSearchFactor = (
	searchId: number,
  callback?: {
    onEditted?: (result: SearchResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchFactorFormData, SearchResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        searchApiBy(searchId, "/factor"),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSearchStatus = (
	searchId: number,
  callback?: {
    onEditted?: (result: SearchResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchStatusFormData, SearchResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        searchApiBy(searchId, "/status"),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSearchsOption = (
	searchId: number,
  callback?: {
    onEditted?: (result: SearchsOptionResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<SearchsOptionFormData, SearchsOptionResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        searchApiBy(searchId, "/option"),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteSearch = (
  callback?: {
    onDeleted?: (searchId: number) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, searchId, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        searchApiBy(searchId),
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useListSearchsGroup = (
  searchId: number | null | undefined,
	deps?: DependencyList
) => {
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return null
				return ApiGetWithToken<GroupResultList>(
					apiAccess,
					searchApiBy(searchId, '/group/list'),
					cancelToken
				)
			},
			[searchId]
		),
		deps
	)
}

export const useGetSearchBunner = (
	searchId?: number | null,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return null
				return ApiGetFileWithToken(
					apiAccess,
					searchApiBy(searchId, '/bunner', { t }),
					cancelToken
				)
			},
			[t, searchId]
		)
	)
}

export const useSetSearchBunner = (
	searchId: number, 
  callback?: {
    onPosted?: (result: FileResponse) => void
    onError?: (reason: Error | undefined) => void
  }
) => {
	return useApiPost<File, FileResponse>(
		useCallback(
			(apiAccess, cancelToken, file, webSocketId) => {
				const formData = new FormData()
				formData.append('file', file)
				return ApiPostFileWithToken(
					apiAccess,
					searchApiBy(searchId, '/bunner'),
					formData,
					cancelToken,
					webSocketId
				)
			},
			[searchId]
		),
		useCallback(
			(result) => {
				if (callback?.onPosted) callback.onPosted(result)
				return result
			},
			[callback]
		),
		useCallback(
			(reason) => {
				if (callback?.onError) callback?.onError(reason)
			},
			[callback]
		)
	)
}

